import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation } from '../utils';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function AccordionGallerySection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const [active, setActive] = useState(0);
    const [lbIndex, setLbIndex] = useState(-1);

    const items = (content.items || []).map(item => ({
        ...item,
        ...resolveItemMedia(item, mediaMode, mediaFolders),
    }));

    if (items.length === 0) {
        return (
            <section className={`py-16 relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <div className="text-center text-gray-400 text-sm">Agrega imagenes para la galeria accordion</div>
            </section>
        );
    }

    const variant = config.variant || 'default';

    // ── Hover-expand variant ──
    if (variant === 'hover-expand') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && (
                        <div className="text-center mb-10">
                            {content.tag && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: s.accent }}>{content.tag}</span>}
                            <h2 className="text-3xl md:text-4xl font-bold mt-2" style={{ color: s.text }}>{content.title}</h2>
                            {content.subtitle && <p className="mt-3 max-w-2xl mx-auto" style={{ color: s.textSub }}>{content.subtitle}</p>}
                        </div>
                    )}
                    <div className="flex gap-1.5 sm:gap-2 md:gap-3 h-[250px] sm:h-[300px] md:h-[450px] lg:h-[500px]">
                        {items.map((item, i) => {
                            const isActive = active === i;
                            const hasMedia = !!(item.resolvedImage || item.carouselImages || item.video);
                            return (
                                <div
                                    key={i}
                                    className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl cursor-pointer transition-all duration-700 ease-in-out"
                                    style={{
                                        flex: isActive ? 5 : 1,
                                        minWidth: isActive ? '50%' : '28px',
                                    }}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setLbIndex(i)}
                                >
                                    {hasMedia ? (
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="" rounded="" className="absolute inset-0 w-full h-full" />
                                    ) : (
                                        <div className="absolute inset-0" style={{ backgroundColor: s.accent, opacity: 0.2 + (i * 0.1) }} />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                    {item.title && (
                                        <div className={`absolute bottom-0 left-0 right-0 p-4 md:p-6 transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 md:opacity-100 translate-y-0'}`}>
                                            <h3 className={`text-white font-bold transition-all duration-700 ${isActive ? 'text-lg md:text-2xl' : 'text-xs md:text-sm'} ${!isActive ? 'md:[writing-mode:vertical-lr] md:rotate-180' : ''}`}>
                                                {item.title}
                                            </h3>
                                            {isActive && item.description && (
                                                <p className="text-white/80 text-sm mt-2 transition-opacity duration-500">{item.description}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={items.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' }))} />
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    // ── Default variant ──
    return (
        <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && (
                    <div className="text-center mb-10">
                        {content.tag && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: s.accent }}>{content.tag}</span>}
                        <h2 className="text-3xl md:text-4xl font-bold mt-2" style={{ color: s.text }}>{content.title}</h2>
                        {content.subtitle && <p className="mt-3 max-w-2xl mx-auto" style={{ color: s.textSub }}>{content.subtitle}</p>}
                    </div>
                )}

                <div className="flex gap-1.5 sm:gap-2 md:gap-3 h-[250px] sm:h-[300px] md:h-[450px] lg:h-[500px]">
                    {items.map((item, i) => {
                        const isActive = active === i;
                        const hasMedia = !!(item.resolvedImage || item.carouselImages || item.video);
                        return (
                            <div
                                key={i}
                                className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl cursor-pointer transition-all duration-500 ease-in-out"
                                style={{
                                    flex: isActive ? 5 : 1,
                                    minWidth: isActive ? '50%' : '28px',
                                }}
                                onClick={() => isActive ? setLbIndex(i) : setActive(i)}
                                onMouseEnter={() => setActive(i)}
                            >
                                {hasMedia ? (
                                    <MediaRenderer
                                        item={item}
                                        fitClass={fitClass}
                                        interval={carouselInterval}
                                        accent={s.accent}
                                        aspect=""
                                        rounded=""
                                        className="absolute inset-0 w-full h-full"
                                    />
                                ) : (
                                    <div className="absolute inset-0" style={{ backgroundColor: s.accent, opacity: 0.2 + (i * 0.1) }} />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                {item.title && (
                                    <div className={`absolute bottom-0 left-0 right-0 p-4 md:p-6 transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0 md:opacity-100'}`}>
                                        <h3 className={`text-white font-bold transition-all duration-500 ${isActive ? 'text-lg md:text-2xl' : 'text-xs md:text-sm'} ${!isActive ? 'md:[writing-mode:vertical-lr] md:rotate-180' : ''}`}>
                                            {item.title}
                                        </h3>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                slides={items.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' }))} />
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
